/**
 * TerraMatrix Admin Panel
 *
 * Centralized admin interface for platform and project management.
 * Platform admins see all tabs; project admins see project-scoped tabs only.
 *
 * Created: December 2025
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useAuthStore } from '@/stores/auth/auth-store';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import {
  AdminDataSourcesTab,
  AdminSchemaGroupsTab,
  AdminSchemasTab,
  AdminOrganizationsTab,
  AdminUsersTab,
  AdminWorkflowEngineTab,
  AdminStorageTab,
  AdminUnifiNetworkTab,
  AdminSystemHealthTab,
  AdminMissionControlTab,
  AdminAIUsageTab,
  OrgBudgetSettings,
} from '@/components/admin';

export const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const isProjectAdmin = useAuthStore((state) => state.isProjectAdmin);
  const isOrgAdmin = useAuthStore((state) => state.isOrgAdmin);
  const { platformAdminEntitlement } = useOrganizationStore();

  const isPlatformLevel = platformAdminEntitlement;
  const canTriageFeedbackAccess = isPlatformLevel;
  const isAdmin = isPlatformLevel || isProjectAdmin || isOrgAdmin;

  // Tab state
  const [activeTab, setActiveTab] = useState('data-sources');

  // Determine visible tabs based on admin type
  const visibleTabs = useMemo(() => {
    const projectTabs = [
      { value: 'data-sources', label: 'Data Sources' },
      { value: 'schema-groups', label: 'Schema Groups' },
      { value: 'schemas', label: 'Schemas' },
      { value: 'workflow-engine', label: 'Workflow Engine' },
      { value: 'storage', label: 'Storage' },
      { value: 'unifi-network', label: 'UniFi Network' },
    ];

    if (isPlatformLevel) {
      // Platform admins see all tabs
      return [
        ...projectTabs.slice(0, 3),
        { value: 'organizations', label: 'Organizations' },
        { value: 'users', label: 'Users' },
        ...projectTabs.slice(3),
        { value: 'system-health', label: 'System Health' },
        { value: 'ai-usage', label: 'AI Usage' },
        { value: 'llm-billing', label: 'LLM Billing' },
        { value: 'missions', label: 'Missions' },
      ];
    }

    return projectTabs;
  }, [isPlatformLevel]);

  // Redirect non-admins
  useEffect(() => {
    if (user && !isAdmin) {
      navigate('/dashboard');
    }
  }, [user, isAdmin, navigate]);

  // Don't render if not admin
  if (!user || !isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Access denied. Admin privileges required.</div>
      </div>
    );
  }

  return (
    <>
      {/* Fixed viewport container */}
      <div className="fixed inset-0 top-[73px] z-10 bg-background flex flex-col">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
          {/* Header with tab navigation - fixed height */}
          <div className="flex-shrink-0 border-b border-border p-4 pb-0">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold">Admin Panel</h1>
                {isPlatformLevel && (
                  <div className="flex items-center gap-1.5 px-2 py-1 bg-amber-500/10 rounded-md">
                    <Shield className="h-4 w-4 text-amber-600" />
                    <span className="text-xs font-medium text-amber-600">Platform Admin</span>
                  </div>
                )}
              </div>
              {canTriageFeedbackAccess && (
                <button
                  type="button"
                  onClick={() => navigate('/admin/feedback')}
                  className="rounded-md border border-border px-3 py-2 text-sm font-medium transition-colors hover:bg-accent"
                >
                  Feedback
                </button>
              )}
            </div>

            {/* Tab Navigation */}
            <TabsList className={`flex w-full overflow-x-auto sm:grid sm:w-auto gap-1`} style={{ gridTemplateColumns: `repeat(${visibleTabs.length}, minmax(0, 1fr))` }}>
              {visibleTabs.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value} className="px-4 sm:px-6 whitespace-nowrap">
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* Tab content area - grows to fill remaining space */}
          <div className="flex-1 min-h-0 overflow-hidden">
            <TabsContent value="data-sources" className="h-full mt-0">
              <AdminDataSourcesTab />
            </TabsContent>

            <TabsContent value="schema-groups" className="h-full mt-0">
              <AdminSchemaGroupsTab />
            </TabsContent>

            <TabsContent value="schemas" className="h-full mt-0">
              <AdminSchemasTab />
            </TabsContent>

            {isPlatformLevel && (
              <>
                <TabsContent value="organizations" className="h-full mt-0">
                  <AdminOrganizationsTab />
                </TabsContent>

                <TabsContent value="users" className="h-full mt-0">
                  <AdminUsersTab />
                </TabsContent>
              </>
            )}

            <TabsContent value="workflow-engine" className="h-full mt-0">
              <AdminWorkflowEngineTab />
            </TabsContent>

            <TabsContent value="storage" className="h-full mt-0">
              <AdminStorageTab />
            </TabsContent>

            {/* UniFi Network Tab */}
            <TabsContent value="unifi-network" className="h-full mt-0">
              <AdminUnifiNetworkTab />
            </TabsContent>

            {/* System Health Tab - Platform Admin Only */}
            {isPlatformLevel && (
              <TabsContent value="system-health" className="h-full mt-0">
                <AdminSystemHealthTab />
              </TabsContent>
            )}

            {/* AI Usage Tab - Platform Admin Only */}
            {isPlatformLevel && (
              <TabsContent value="ai-usage" className="h-full mt-0">
                <AdminAIUsageTab />
              </TabsContent>
            )}

            {/* LLM Billing Tab - Platform Admin Only */}
            {isPlatformLevel && (
              <TabsContent value="llm-billing" className="h-full mt-0">
                <OrgBudgetSettings />
              </TabsContent>
            )}

            {/* Mission Control Tab - Platform Admin Only */}
            {isPlatformLevel && (
              <TabsContent value="missions" className="h-full mt-0">
                <AdminMissionControlTab />
              </TabsContent>
            )}
          </div>
        </Tabs>
      </div>
    </>
  );
};

export default AdminPage;
